import { useState } from 'react';
import { useAuth } from '../hooks/useAuth';
import { Alert, Button, Dialog, DialogBody, DialogFooter, DialogFooterSpacer } from './ui';

const count = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;

/**
 * Asks the account just signed into whether to add what was made on this phone without one
 * (docs/plans/local-mode.md): add it, leave it on the phone for now, or delete it. Never done
 * unasked: whoever signs in may not be who used the phone. Deleting asks once more, as it can't be
 * undone.
 */
export function LocalDataOfferDialog() {
  const { user, localDataOffer, addLocalDataToAccount, keepLocalDataOnPhone, deleteLocalData } = useAuth();
  const [busy, setBusy] = useState<'adding' | 'deleting' | null>(null);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [failed, setFailed] = useState(false);

  if (!localDataOffer) return null;

  const run = async (what: 'adding' | 'deleting', action: () => Promise<boolean>) => {
    setBusy(what);
    setFailed(false);
    const ok = await action();
    setBusy(null);
    setFailed(!ok);
    if (ok) setConfirmingDelete(false);
  };

  const what = `${count(localDataOffer.households, 'household', 'households')} and ${count(localDataOffer.chores, 'chore', 'chores')}`;

  return (
    <Dialog
      open
      onClose={() => !busy && keepLocalDataOnPhone()}
      title={confirmingDelete ? `Delete ${what}?` : "Add what's on this phone?"}
      size="sm"
      hideClose
    >
      <DialogBody className="space-y-3 pt-1 text-sm text-gray-600 dark:text-gray-400">
        {confirmingDelete ? (
          <p>They&apos;ll be deleted from this phone, with everything done in them. This can&apos;t be undone.</p>
        ) : (
          <>
            <p>
              You made {what} on this phone before signing in. Add them to your account
              {user?.email ? ` (${user.email})` : ''}, where they sync and can be shared?
            </p>
            <p>If you choose Not now, they stay on this phone and come back if you sign out.</p>
          </>
        )}
        {failed && (
          <Alert tone="error">
            They couldn&apos;t be {confirmingDelete ? 'deleted' : 'added'}. Nothing has changed; try again.
          </Alert>
        )}
      </DialogBody>
      <DialogFooter>
        {confirmingDelete ? (
          <>
            <Button variant="ghost" onClick={() => { setConfirmingDelete(false); setFailed(false); }} disabled={!!busy}>
              Back
            </Button>
            <Button variant="danger" onClick={() => void run('deleting', deleteLocalData)} loading={busy === 'deleting'}>
              Delete them
            </Button>
          </>
        ) : (
          <>
            <Button variant="danger-ghost" onClick={() => { setConfirmingDelete(true); setFailed(false); }} disabled={!!busy}>
              Delete them
            </Button>
            <DialogFooterSpacer />
            <Button variant="ghost" onClick={keepLocalDataOnPhone} disabled={!!busy}>Not now</Button>
            <Button variant="primary" onClick={() => void run('adding', addLocalDataToAccount)} loading={busy === 'adding'}>
              Add to my account
            </Button>
          </>
        )}
      </DialogFooter>
    </Dialog>
  );
}
